import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL as CONFIG_API_URL } from "../../config";
import { Search, Save, Trash2, CheckCircle2, Clock, Globe, Plus, Loader2 } from 'lucide-react';


const API_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

export default function MediaDomainDict() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'mapped', 'unmapped'
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/media-domain-dict?`;
      if (activeTab === 'mapped') url += 'isMapped=true&';
      if (activeTab === 'unmapped') url += 'isMapped=false&';
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}`;
      
      const token = localStorage.getItem('token');
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const json = await res.json();
      if (json.success) {
        setData(json.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditName(item.mediaName || '');
  };

  const saveEdit = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/media-domain-dict/${id}`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mediaName: editName, isMapped: true })
      });
      const json = await res.json();
      if (json.success) {
        setEditingId(null);
        fetchData();
      } else {
        alert("保存失败");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("确定删除此映射规则吗？")) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/media-domain-dict/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        fetchData();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    
    <div className="flex-1 bg-gray-50 dark:bg-slate-900 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-6 md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate flex items-center">
              <Globe className="w-8 h-8 mr-3 text-indigo-500" />
              信源字典管理
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
              管理和标准化引擎返回的引用网页来源名称，提升大盘分析的准确度。
            </p>
          </div>
        </div>

      
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
        {/* Header Actions */}
        <div className="p-5 border-b border-gray-100 dark:border-slate-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex bg-gray-100/50 dark:bg-slate-900/50 p-1 rounded-lg w-max border border-gray-200/50 dark:border-slate-700/50">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === 'all' 
                  ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm' 
                  : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200 hover:bg-gray-200/50 dark:hover:bg-slate-700/50'
              }`}
            >
              全部字典
            </button>
            <button
              onClick={() => setActiveTab('unmapped')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center ${
                activeTab === 'unmapped' 
                  ? 'bg-white dark:bg-slate-800 text-orange-600 dark:text-orange-400 shadow-sm' 
                  : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200 hover:bg-gray-200/50 dark:hover:bg-slate-700/50'
              }`}
            >
              <Clock size={14} className="mr-1.5" />自动发现 (待确认)
            </button>
            <button
              onClick={() => setActiveTab('mapped')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center ${
                activeTab === 'mapped' 
                  ? 'bg-white dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 shadow-sm' 
                  : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200 hover:bg-gray-200/50 dark:hover:bg-slate-700/50'
              }`}
            >
              <CheckCircle2 size={14} className="mr-1.5" />已映射
            </button>
          </div>
          
          <form onSubmit={handleSearch} className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="搜索域名或名称..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-gray-50 dark:bg-slate-900/50 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
            />
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
          </form>
        </div>

        {/* Table */}
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-left text-sm text-gray-600 dark:text-slate-300">
            <thead className="bg-gray-50 dark:bg-slate-900/50 text-gray-700 dark:text-slate-200 uppercase text-xs font-semibold border-b border-gray-100 dark:border-slate-700/50">
              <tr>
                <th className="px-6 py-4 rounded-tl-xl w-1/3">原始域名 (URL)</th>
                <th className="px-6 py-4 w-1/3">映射标准名称 (媒体名)</th>
                <th className="px-6 py-4">状态</th>
                <th className="px-6 py-4 text-right rounded-tr-xl">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-500" />
                    加载中...
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-400">
                    <Globe className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    没有找到对应的域名记录
                  </td>
                </tr>
              ) : (
                data.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4 font-mono text-xs text-indigo-600 dark:text-indigo-400">
                      {item.domain}
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-slate-100">
                      {editingId === item.id ? (
                        <div className="flex items-center gap-2">
                          <input 
                            type="text" 
                            className="px-2 py-1 border border-indigo-300 dark:border-indigo-500/50 rounded-md bg-white dark:bg-slate-900 text-sm w-full outline-none focus:ring-2 focus:ring-indigo-500/20"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="如：知乎, 汽车之家"
                            autoFocus
                            onKeyDown={(e) => { if(e.key === 'Enter') saveEdit(item.id); if(e.key === 'Escape') setEditingId(null); }}
                          />
                        </div>
                      ) : (
                        item.mediaName || <span className="text-gray-400 italic">待完善</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {item.isMapped ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20">
                          已确认
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20">
                          待确认
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {editingId === item.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => saveEdit(item.id)} className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 font-medium text-sm px-2 py-1 bg-indigo-50 dark:bg-indigo-500/10 rounded">保存</button>
                          <button onClick={() => setEditingId(null)} className="text-gray-500 hover:text-gray-700 dark:text-slate-400 text-sm px-2 py-1">取消</button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-3">
                          <button onClick={() => startEdit(item)} className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 hover:underline text-sm font-medium">
                            编辑名称
                          </button>
                          <button onClick={() => deleteItem(item.id)} className="text-red-500 hover:text-red-700 dark:text-red-400/80 hover:dark:text-red-400 transition-colors">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
          </div>
    </div>
  );
}
